import React, { useState, useEffect } from "react";
import {
  Edit16Regular,
  Delete16Regular,
  EmojiAdd16Regular,
  ArrowClockwise16Regular,
  Document16Regular,
  ArrowReply16Regular,
  Pin16Regular,
  PinOff16Regular,
  FolderArrowRight16Regular,
} from "@fluentui/react-icons";
import { chatService, type ChatMessage } from "../../services/chatService";
import { AttachmentAssignDialog } from "./AttachmentAssignDialog";
import { InitialsAvatar } from "./chatUi";
import { AUB, INK, INK2, LINE, BG_REPLY, PLUM, fmtTime } from "./chatTheme";
import { alleDokumente, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

const EMOJI_PALETTE = ["👍", "❤️", "😂", "🎉", "🙏", "👀"];

const formatBytes = (n: number): string => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

// URL-Erkennung: macht http(s)-Links im Nachrichtentext klickbar.
const URL_RE = /(https?:\/\/[^\s]+)/g;
const renderBody = (text: string, linkColor: string): React.ReactNode =>
  text.split(URL_RE).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: linkColor, textDecoration: "underline" }}>
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );

// Lese-Haken in der ausgehenden Bubble: zwei Haken = gelesen, einer = erhalten/ungelesen.
// `read` reines Weiß, sonst leicht gedämpftes Weiß.
const ReadTicks: React.FC<{ read: boolean }> = ({ read }) => {
  const color = read ? "#fff" : "rgba(255,255,255,0.82)";
  return read ? (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden style={{ display: "block" }}>
      <path d="M1 6 L4 9 L9 2.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 6 L10 9 L16 1.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" aria-hidden style={{ display: "block" }}>
      <path d="M1 6 L4.5 9.5 L11 1.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

type Props = {
  message: ChatMessage;
  isOwn: boolean;
  authorName: string;
  showHeader: boolean;
  reactions: Array<{ emoji: string; count: number; userIds: string[] }>;
  onEdit: (id: string, body: string) => void;
  onDelete: (id: string) => void;
  onToggleReaction: (id: string, emoji: string) => void;
  onRetry: (clientMsgId: string) => void;
  onReply: (message: ChatMessage) => void;
  onTogglePin: (id: string, pinned: boolean) => void;
  readReceiptLabel?: string | null;
};

export const MessageBubble: React.FC<Props> = ({
  message,
  isOwn,
  authorName,
  showHeader,
  reactions,
  onEdit,
  onDelete,
  onToggleReaction,
  onRetry,
  onReply,
  onTogglePin,
  readReceiptLabel,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [showEmoji, setShowEmoji] = useState(false);
  // Rechtsklick-Kontextmenü (ersetzt die frühere Hover-Aktionsleiste).
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  /** Offener „Datei zuweisen"-Dialog (Anhang-Id + Dateiname). */
  const [assign, setAssign] = useState<{ id: string; name: string } | null>(null);
  const deleted = !!message.deletedAt;

  const saveEdit = () => {
    const body = draft.trim();
    if (body && body !== message.body) onEdit(message.id, body);
    setEditing(false);
  };

  // Server-bestätigte Nachricht (hat seq) → Reaktion/Antworten/Pin möglich.
  const canServerAct = !deleted && !!message.seq;
  const canEdit =
    isOwn && !deleted && message._status !== "sending" && message._status !== "failed";
  const hasMenu = canServerAct || canEdit;

  const openMenu = (e: React.MouseEvent) => {
    if (!hasMenu) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };
  const closeMenu = () => setMenu(null);

  // Escape schließt das Kontextmenü (Klick außerhalb über die Overlay-Fläche).
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    alleDokumente.addEventListener("keydown", onKey);
    return () => alleDokumente.removeEventListener("keydown", onKey);
  }, [menu]);

  // Lese-Haken: Pro-Nachricht-Lesehorizont wird nicht durchgereicht — `readReceiptLabel`
  // ist nur für die jeweils letzte eigene Nachricht gesetzt und genau dann „gelesen".
  // Konservative Ableitung: Label vorhanden → ✓✓ (gelesen), sonst ✓ (erhalten/gesendet).
  const showTicks =
    isOwn && !deleted && message._status !== "sending" && message._status !== "failed";
  const ticksRead = !!readReceiptLabel;

  // Sprechblasen-Optik: eigene Nachrichten rechts (dunkles Plum), fremde links (Weiß + zarte Border).
  const align = isOwn ? "flex-end" : "flex-start";
  const bubbleStyle: React.CSSProperties = {
    display: "inline-block",
    maxWidth: "100%",
    padding: "9px 13px",
    borderRadius: 16,
    fontSize: 13.5,
    background: isOwn ? PLUM : "#fff",
    color: isOwn ? "#fff" : INK,
    border: isOwn ? "none" : "1px solid #efeaf0",
    opacity: message._status === "sending" ? 0.6 : 1,
    cursor: hasMenu ? "context-menu" : "default",
  };

  // Reply-Zitat: in der eigenen Plum-Bubble hell/lesbar, sonst dunkel.
  const quoteBorder = isOwn ? "rgba(255,255,255,0.55)" : LINE;
  const quoteAuthorColor = isOwn ? "#f3d9f7" : AUB;
  const quoteTextColor = isOwn ? "rgba(255,255,255,0.72)" : INK2;

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        marginTop: showHeader ? 12 : 2,
        alignItems: "flex-start",
        flexDirection: isOwn ? "row-reverse" : "row",
      }}
    >
      {/* Avatar-Spalte nur bei fremden Nachrichten — echtes Entra-Foto via userId, Fallback Initialen.
          Bei eigenen Nachrichten entfällt die Spalte komplett, damit die Bubble (row-reverse)
          wirklich am rechten Rand anliegt statt 34px + gap davor zu enden.
          Der leere Platzhalter (ohne showHeader) bleibt für die Einrückung von Folgeblasen. */}
      {!isOwn && (
        <div style={{ width: 34, flexShrink: 0 }}>
          {showHeader && (
            <InitialsAvatar name={authorName} userId={message.authorUserId} size={34} />
          )}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: align }}>
        {showHeader && !isOwn && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, justifyContent: align }}>
            <span style={{ fontWeight: 700, color: INK, fontSize: 13.5, overflowWrap: "anywhere" }}>
              {authorName}
            </span>
          </div>
        )}

        {message.pinned && !deleted && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, color: AUB, fontSize: 11, marginBottom: 2 }}>
            <Pin16Regular /> angeheftet
          </div>
        )}

        {deleted ? (
          <div style={{ fontSize: 13, color: INK2, fontStyle: "italic" }}>Nachricht gelöscht</div>
        ) : editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
            <textarea
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  saveEdit();
                }
                if (e.key === "Escape") setEditing(false);
              }}
              rows={2}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "8px 10px",
                border: `1px solid ${LINE}`,
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 13.5,
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={saveEdit} style={{ border: `1px solid ${PLUM}`, background: PLUM, color: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>
                Speichern
              </button>
              <button type="button" onClick={() => setEditing(false)} style={{ border: `1px solid ${LINE}`, background: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: "78%" }}>
            <div style={bubbleStyle} onContextMenu={openMenu}>
              {/* Reply-Zitat innerhalb der Bubble (hell in Plum, dunkel in Weiß). */}
              {message.replyTo && (
                <div
                  style={{
                    borderLeft: `3px solid ${quoteBorder}`,
                    paddingLeft: 8,
                    marginBottom: 5,
                    fontSize: 12,
                    lineHeight: 1.4,
                    overflowWrap: "anywhere",
                  }}
                >
                  <span style={{ fontWeight: 600, color: quoteAuthorColor }}>
                    {message.replyTo.authorName ?? "Unbekannt"}:{" "}
                  </span>
                  <span style={{ color: quoteTextColor }}>
                    {message.replyTo.deleted ? "Nachricht gelöscht" : message.replyTo.body}
                  </span>
                </div>
              )}
              <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.5 }}>
                {renderBody(message.body, isOwn ? "#f3d9f7" : AUB)}
                {message.editedAt && (
                  <span style={{ color: isOwn ? "rgba(255,255,255,0.7)" : INK2, fontSize: 11, marginLeft: 6 }}>(bearbeitet)</span>
                )}
                {/* Uhrzeit + Lese-Haken am Ende der Bubble. */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    marginLeft: 8,
                    verticalAlign: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ color: isOwn ? "rgba(255,255,255,0.88)" : INK2, fontSize: 10.5 }}>
                    {fmtTime(message.createdAt)}
                  </span>
                  {showTicks && <ReadTicks read={ticksRead} />}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* Anhänge — Klick öffnet die Weiterverwendung (Vorgang zuweisen, dann
            herunterladen) statt sofort zu laden; Bilder behalten ihre Vorschau
            und bekommen die Aktion als Zeile darunter. */}
        {!deleted && message.attachments && message.attachments.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6, justifyContent: align }}>
            {message.attachments.map((a) => {
              const url = chatService.attachmentDownloadUrl(a.id);
              const isImage = (a.mimeType ?? "").startsWith("image/");
              return isImage ? (
                <div key={a.id} style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: align === "flex-end" ? "flex-end" : "flex-start" }}>
                  <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={a.name} loading="lazy" style={{ maxWidth: 240, maxHeight: 200, borderRadius: 8, border: `1px solid ${LINE}`, display: "block" }} />
                  </a>
                  <button
                    type="button"
                    onClick={() => setAssign({ id: a.id, name: a.name })}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: INK2, fontSize: 11.5, cursor: "pointer", padding: 0, fontFamily: "inherit" }}
                  >
                    <FolderArrowRight16Regular />
                    Zuweisen
                  </button>
                </div>
              ) : (
                <button
                  key={a.id}
                  type="button"
                  title="Einem Vorgang zuweisen oder herunterladen"
                  onClick={() => setAssign({ id: a.id, name: a.name })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, maxWidth: 260, border: `1px solid ${LINE}`, borderRadius: 8, padding: "6px 10px", color: INK, background: BG_REPLY, cursor: "pointer", fontFamily: "inherit", fontSize: 13, textAlign: "left" }}
                >
                  <Document16Regular />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                  <span style={{ color: INK2, fontSize: 11, flexShrink: 0 }}>{formatBytes(a.size)}</span>
                </button>
              );
            })}
          </div>
        )}

        {assign && (
          <AttachmentAssignDialog
            attachmentId={assign.id}
            fileName={assign.name}
            onClose={() => setAssign(null)}
          />
        )}

        {/* Reaktionen (Chips + Emoji-Palette über das Kontextmenü ausgelöst) */}
        {!deleted && (reactions.length > 0 || showEmoji) && (
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", alignItems: "center", justifyContent: align }}>
            {reactions.map((r) => (
              <button key={r.emoji} type="button" onClick={() => onToggleReaction(message.id, r.emoji)} style={{ border: `1px solid ${LINE}`, background: BG_REPLY, borderRadius: 999, padding: "1px 8px", fontSize: 12.5, cursor: "pointer" }}>
                {r.emoji} {r.count}
              </button>
            ))}
            {showEmoji &&
              EMOJI_PALETTE.map((e) => (
                <button key={e} type="button" aria-label={`Mit ${e} reagieren`} onClick={() => { onToggleReaction(message.id, e); setShowEmoji(false); }} style={{ border: "none", background: "transparent", fontSize: 16, cursor: "pointer", lineHeight: 1 }}>
                  {e}
                </button>
              ))}
          </div>
        )}

        {/* Erneut senden bleibt sichtbar (Statushinweis bei Fehlversand). */}
        {message._status === "failed" && (
          <div style={{ display: "flex", marginTop: 3, justifyContent: align }}>
            <button type="button" onClick={() => onRetry(message.clientMsgId)} style={{ border: "none", background: "transparent", color: "#b91c1c", cursor: "pointer", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3 }}>
              <ArrowClockwise16Regular /> Erneut senden
            </button>
          </div>
        )}
      </div>

      {/* Kontextmenü (Rechtsklick): Reaktion · Antworten · Pin · Bearbeiten · Löschen */}
      {menu && (
        <>
          <div
            onPointerDown={closeMenu}
            onContextMenu={(e) => {
              e.preventDefault();
              closeMenu();
            }}
            style={{ position: "fixed", inset: 0, zIndex: 1000 }}
          />
          <div
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: Math.min(menu.y, aktivesFenster().innerHeight - 230),
              left: Math.min(menu.x, aktivesFenster().innerWidth - 200),
              zIndex: 1001,
              minWidth: 178,
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(20,8,20,0.18)",
              padding: 4,
            }}
          >
            {canServerAct && (
              <CtxItem icon={<EmojiAdd16Regular />} label="Reaktion" onClick={() => { setShowEmoji(true); closeMenu(); }} />
            )}
            {canServerAct && (
              <CtxItem icon={<ArrowReply16Regular />} label="Antworten" onClick={() => { onReply(message); closeMenu(); }} />
            )}
            {canServerAct && (
              <CtxItem
                icon={message.pinned ? <PinOff16Regular /> : <Pin16Regular />}
                label={message.pinned ? "Lösen" : "Anheften"}
                onClick={() => { onTogglePin(message.id, !message.pinned); closeMenu(); }}
              />
            )}
            {canEdit && (
              <CtxItem icon={<Edit16Regular />} label="Bearbeiten" onClick={() => { setDraft(message.body); setEditing(true); closeMenu(); }} />
            )}
            {canEdit && (
              <CtxItem icon={<Delete16Regular />} label="Löschen" danger onClick={() => { onDelete(message.id); closeMenu(); }} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Eintrag im Rechtsklick-Kontextmenü mit dezentem Hover.
const CtxItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}> = ({ icon, label, danger, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = BG_REPLY;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 9,
      width: "100%",
      textAlign: "left",
      border: "none",
      background: "transparent",
      borderRadius: 7,
      padding: "7px 10px",
      fontSize: 13,
      fontFamily: "inherit",
      color: danger ? "#b91c1c" : INK,
      cursor: "pointer",
    }}
  >
    {icon}
    <span>{label}</span>
  </button>
);

export default MessageBubble;
